import * as React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Spinner,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { CheckCircle, Sparkle, Warning } from 'phosphor-react';
import {
  IIaResultado,
  IRecorteConciliar,
  useContaCilConciliarAuto,
  useContaCilIaConciliar,
  useContaCilIaConciliarPreview,
  useContaCilIaGerar,
} from '../../../../../../hooks/useContaCilRegras';

const C = {
  indigo: '#6366F1',
  pink: '#EC4899',
  green: '#10B981',
  amber: '#F59E0B',
  red: '#EF4444',
  ink: '#0F172A',
  muted: '#64748B',
  subtle: '#94A3B8',
};

// 1 chamada processa ate este tanto (o back lota 60/lote internamente e
// clampa em 180). 60 = 1 lote (~10s) por requisicao -> barra atualiza mais
// vezes (mais progressiva) e cada chamada fica bem sob o timeout de proxy.
const LIMITE_POR_CHAMADA = 60;

const fmtUsd = (v: number) => `US$ ${v.toFixed(2)}`;
const fmtNum = (v: number) => new Intl.NumberFormat('pt-BR').format(v);

type Fase = 'idle' | 'running' | 'done' | 'error';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  empresaId: number | null;
  empresaLabel?: string;
  /** Recorte ativo na tela (ano/mês/banco/conta). A contagem do modal e a
   *  execução usam o MESMO recorte — o que está na tela é o que será tocado. */
  recorte?: IRecorteConciliar;
}

const miniCard = (label: string, valor: React.ReactNode, sub?: string, accent?: string) => (
  <Box
    flex="1"
    borderWidth="1px"
    borderColor="rgba(15,23,42,0.08)"
    borderRadius="xl"
    bg="rgba(255,255,255,0.5)"
    px={4}
    py={3}
  >
    <Text
      fontSize="10px"
      fontWeight="600"
      color={C.subtle}
      textTransform="uppercase"
      letterSpacing="0.06em"
    >
      {label}
    </Text>
    <Text fontSize="24px" fontWeight="700" color={accent ?? C.ink} lineHeight="1.2">
      {valor}
    </Text>
    {sub && (
      <Text fontSize="11px" color={C.muted}>
        {sub}
      </Text>
    )}
  </Box>
);

export function AutoConciliarModal({ isOpen, onClose, empresaId, empresaLabel, recorte }: Props) {
  const toast = useToast();
  const preview = useContaCilIaConciliarPreview(empresaId, isOpen, recorte);
  const conciliarAuto = useContaCilConciliarAuto();
  const iaConc = useContaCilIaConciliar();
  const iaGerar = useContaCilIaGerar();

  const [pct, setPct] = React.useState(100);
  const [gerarAntes, setGerarAntes] = React.useState(true);
  const [fase, setFase] = React.useState<Fase>('idle');
  const [prog, setProg] = React.useState({ processados: 0, conciliados: 0, custo: 0, lote: 0 });
  // Conciliados pelo motor GRATIS (conciliar_auto) — pra mostrar o breakdown
  // gratis vs IA no fim (e deixar claro que parte saiu sem custo).
  const [gratis, setGratis] = React.useState(0);
  // Alvo CONGELADO no inicio do run — o `alvo` derivado do preview muda quando
  // o preview refetcha (pendentes caem), o que zerava a barra. Use este.
  const [alvoRun, setAlvoRun] = React.useState(0);
  const [faseTxt, setFaseTxt] = React.useState('');
  const [erro, setErro] = React.useState<string | null>(null);
  const cancelRef = React.useRef(false);

  const pend = preview.data?.pendentes ?? 0;
  const alvo = Math.round((pend * pct) / 100);
  const nLotes = Math.max(1, Math.ceil(alvo / (preview.data?.chunk ?? 60)));
  const custoMin = nLotes * (preview.data?.custo_lote_min ?? 0.05);
  const custoMax = nLotes * (preview.data?.custo_lote_max ?? 0.1);
  const tempoEstSeg = Math.ceil((alvo / LIMITE_POR_CHAMADA) * 22); // ~22s por chamada de 120

  // Reset ao abrir/trocar empresa.
  React.useEffect(() => {
    if (isOpen) {
      setFase('idle');
      setProg({ processados: 0, conciliados: 0, custo: 0, lote: 0 });
      setGratis(0);
      setAlvoRun(0);
      setFaseTxt('');
      setErro(null);
      setPct(100);
      cancelRef.current = false;
    }
  }, [isOpen, empresaId]);

  const totalLotesRun = Math.max(1, Math.ceil(alvoRun / LIMITE_POR_CHAMADA));
  const barPctProc =
    alvoRun > 0 ? Math.min(100, Math.round((prog.processados / alvoRun) * 100)) : 0;
  // No 'done' forca 100%: o motor gratis pode resolver tudo antes da IA, deixando
  // processados < alvoRun (IA quebra cedo em restantes_pendentes=0) — sem isso a
  // barra travava num pct < 100 mesmo com a conciliacao concluida.
  const barPct = fase === 'done' ? 100 : barPctProc;
  const conciliadosIa = Math.max(0, prog.conciliados - gratis);
  // Mensagem de "0 conciliados" data-driven: so culpa a config se a conta-banco
  // NAO estiver vinculada. Vinculada => o motivo e contrapartida indeterminavel
  // (Pix a PF), nao config — nao adianta mandar checar a aba Contas.
  const semConciliarTxt =
    preview.data?.tem_conta_vinculada === false
      ? 'Nenhum conciliado — vincule a conta bancária ao plano de contas na aba Contas.'
      : 'Nenhum conciliado neste lote. O banco já está resolvido; o que falta é a contrapartida — comum em Pix a pessoas físicas, que a IA não arrisca sozinha. Classifique alguns manualmente: a memória aprende e passa a conciliar parecidos automaticamente.';

  const podeFechar = fase !== 'running';

  const rodar = async () => {
    if (!empresaId || alvo <= 0) return;
    const alvoFixo = alvo; // congela: o preview vai mudar durante/apos o run
    setAlvoRun(alvoFixo);
    setFase('running');
    setErro(null);
    setGratis(0);
    cancelRef.current = false;
    let conciliados = 0;
    let gratisN = 0;
    let custo = 0;
    let processados = 0;
    let lote = 0;
    const totalLotes = Math.max(1, Math.ceil(alvoFixo / LIMITE_POR_CHAMADA));
    try {
      // 1) Motor deterministico GRATIS antes de gastar token: transferencias,
      //    socio/cliente, regras P0 e memoria L2. Pega o resolvivel de graca.
      //    Best-effort: se falhar, a IA ainda roda (so paga mais).
      setFaseTxt('Conciliação automática (grátis)…');
      try {
        const auto = await conciliarAuto.mutateAsync({ empresaId, recorte });
        gratisN = auto.conciliados;
        conciliados += auto.conciliados;
        setGratis(gratisN);
        setProg({ processados, conciliados, custo, lote });
      } catch {
        // motor gratis e otimizacao; nao bloqueia a conciliacao por IA
      }

      if (gerarAntes && !cancelRef.current) {
        setFaseTxt('Gerando regras novas com IA…');
        const g: IIaResultado = await iaGerar.mutateAsync(empresaId);
        conciliados += g.conciliados;
        custo += g.custo_usd ?? 0;
        setProg({ processados, conciliados, custo, lote });
      }
      // 2) IA lote a lote pro restante (contrapartida que so o modelo resolve).
      while (processados < alvoFixo && !cancelRef.current) {
        const restante = alvoFixo - processados;
        const limite = Math.min(LIMITE_POR_CHAMADA, restante);
        lote += 1;
        setFaseTxt(`Conciliando lote ${lote} de ${totalLotes}…`);
        // eslint-disable-next-line no-await-in-loop
        const r: IIaResultado = await iaConc.mutateAsync({ empresaId, limite, recorte });
        processados += r.analisados;
        conciliados += r.conciliados;
        custo += r.custo_usd ?? 0;
        setProg({ processados, conciliados, custo, lote });
        // Acabaram os pendentes (back nao retornou nada) ou zerou restantes.
        if (r.analisados === 0 || (r.restantes_pendentes ?? 0) === 0) break;
      }
      setProg({ processados, conciliados, custo, lote });
      setFaseTxt(cancelRef.current ? 'Cancelado' : 'Concluído');
      setFase('done');
      preview.refetch();
    } catch (e: any) {
      const msg =
        e?.response?.status === 409
          ? 'Já há uma conciliação por IA rodando nesta empresa.'
          : e?.response?.data?.detail || 'Falha ao conciliar com a IA.';
      setErro(typeof msg === 'string' ? msg : 'Falha ao conciliar com a IA.');
      setFase('error');
    }
  };

  const fechar = () => {
    if (!podeFechar) return;
    if (fase === 'done' || fase === 'error') {
      toast({
        title: `${prog.conciliados} conciliados`,
        description: `${gratis} grátis + ${conciliadosIa} IA · custo ${fmtUsd(prog.custo)}`,
        status: fase === 'done' ? 'success' : 'warning',
        duration: 6000,
      });
    }
    onClose();
  };

  const renderHeaderIcon = () => {
    if (fase === 'done') return <CheckCircle size={24} weight="fill" />;
    if (fase === 'error') return <Warning size={24} weight="fill" />;
    return <Sparkle size={22} weight="fill" />;
  };
  const headerTitulo = () => {
    if (fase === 'running') return 'Conciliando com IA…';
    if (fase === 'done') return 'Conciliação concluída';
    return 'Auto-conciliar com IA';
  };
  const renderBody = () => {
    if (preview.isLoading) {
      return (
        <Flex py={10} justify="center" align="center" gap={3} color={C.muted}>
          <Spinner size="sm" /> <Text fontSize="sm">Carregando…</Text>
        </Flex>
      );
    }
    if (preview.data && !preview.data.pode_usar_ia) {
      return (
        <Box py={6} textAlign="center" color={C.amber}>
          <Text fontWeight="600">{preview.data.motivo_bloqueio}</Text>
        </Box>
      );
    }
    if (fase === 'idle') {
      return (
        <>
          <Flex gap={3} mb={5}>
            {miniCard('Pendentes p/ IA', fmtNum(pend), 'contrapartida vazia')}
            {miniCard(
              'Já conciliados IA',
              fmtNum(preview.data?.ja_conciliados_ia ?? 0),
              'não reprocessam',
              C.indigo,
            )}
          </Flex>

          <Flex justify="space-between" align="center" mb={1}>
            <Text fontSize="13px" fontWeight="600" color={C.ink}>
              Quanto conciliar agora
            </Text>
            <Text fontSize="13px" fontWeight="700" color={C.indigo}>
              {pct}%
            </Text>
          </Flex>
          <Slider
            aria-label="percentual"
            value={pct}
            onChange={setPct}
            min={0}
            max={100}
            step={5}
            isDisabled={pend === 0}
          >
            <SliderTrack bg="rgba(99,102,241,0.12)" h="6px" borderRadius="full">
              <SliderFilledTrack bgGradient={`linear(90deg, ${C.indigo}, ${C.pink})`} />
            </SliderTrack>
            <Tooltip label={`${pct}%`} placement="top" isOpen>
              <SliderThumb
                boxSize="20px"
                boxShadow={`0 2px 8px -2px ${C.indigo}`}
                borderWidth="2px"
                borderColor={C.indigo}
              />
            </Tooltip>
          </Slider>

          <Box
            mt={4}
            borderWidth="1px"
            borderColor="rgba(99,102,241,0.18)"
            borderRadius="xl"
            bg="rgba(99,102,241,0.05)"
            px={4}
            py={3}
          >
            <Text fontSize="13px" color={C.ink}>
              Vai processar <b>~{fmtNum(alvo)}</b> lançamentos · ~{nLotes} lote(s) · ⏱ ~
              {tempoEstSeg < 60 ? `${tempoEstSeg}s` : `${Math.ceil(tempoEstSeg / 60)}min`}
            </Text>
            <Text fontSize="13px" color={C.indigo} fontWeight="600" mt={1}>
              💲 Custo estimado: até {fmtUsd(custoMax)}{' '}
              <Text as="span" color={C.muted} fontWeight="400">
                (faixa {fmtUsd(custoMin)}–{fmtUsd(custoMax)})
              </Text>
            </Text>
            <Text fontSize="12px" color={C.green} fontWeight="500" mt={1.5}>
              ✓ Transferências, sócios e regras conhecidas conciliam grátis primeiro — o custo real
              costuma ficar abaixo da estimativa.
            </Text>
          </Box>

          <Checkbox
            mt={4}
            isChecked={gerarAntes}
            onChange={e => setGerarAntes(e.target.checked)}
            colorScheme="purple"
            size="sm"
          >
            <Text fontSize="13px" color={C.muted}>
              Gerar regras novas antes (pegam os próximos imports automaticamente)
            </Text>
          </Checkbox>
        </>
      );
    }
    return (
      <Box py={2}>
        <Flex justify="space-between" align="center" mb={2}>
          <Text fontSize="13px" fontWeight="600" color={C.ink}>
            {faseTxt || 'Processando…'}
          </Text>
          <Text fontSize="14px" fontWeight="700" color={C.indigo}>
            {barPct}%
          </Text>
        </Flex>
        <Box
          position="relative"
          h="12px"
          borderRadius="full"
          bg="rgba(99,102,241,0.12)"
          overflow="hidden"
        >
          <Box
            position="relative"
            h="100%"
            bgGradient={`linear(90deg, ${C.indigo}, ${C.pink})`}
            borderRadius="full"
            transition="width 0.5s cubic-bezier(0.4,0,0.2,1)"
            w={`${barPct}%`}
          >
            {fase === 'running' && (
              <Box
                position="absolute"
                inset="0"
                bgGradient="linear(90deg, transparent, rgba(255,255,255,0.5), transparent)"
                bgSize="200% 100%"
                sx={{
                  animation: 'acShimmer 1.1s linear infinite',
                  '@keyframes acShimmer': {
                    '0%': { backgroundPosition: '200% 0' },
                    '100%': { backgroundPosition: '-200% 0' },
                  },
                }}
              />
            )}
          </Box>
        </Box>
        <Text fontSize="11px" color={C.subtle} mt={1.5}>
          {fase === 'running'
            ? `${fmtNum(prog.processados)} de ${fmtNum(alvoRun)} processados${
                prog.lote > 0 ? ` · lote ${prog.lote} de ${totalLotesRun}` : ''
              }`
            : `${fmtNum(prog.conciliados)} conciliados no total`}
        </Text>

        <Flex gap={3} mt={4}>
          {miniCard('Processados pela IA', fmtNum(prog.processados))}
          {miniCard(
            'Conciliados',
            fmtNum(prog.conciliados),
            fase === 'done' && prog.conciliados > 0
              ? `${fmtNum(gratis)} grátis · ${fmtNum(conciliadosIa)} IA`
              : undefined,
            C.green,
          )}
          {miniCard('Custo', fmtUsd(prog.custo))}
        </Flex>
        {fase === 'error' && (
          <Text mt={4} fontSize="13px" color={C.red} textAlign="center">
            {erro}
          </Text>
        )}
        {fase === 'running' && (
          <Text mt={4} fontSize="12px" color={C.muted} textAlign="center">
            A IA trabalha lote a lote. Você pode cancelar; o lote atual termina.
          </Text>
        )}
        {fase === 'done' && prog.conciliados === 0 && prog.processados > 0 && (
          <Text mt={4} fontSize="12px" color={C.amber} textAlign="center">
            {semConciliarTxt}
          </Text>
        )}
      </Box>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={fechar}
      isCentered
      size={{ base: 'full', md: 'lg' }}
      motionPreset="slideInBottom"
      closeOnOverlayClick={podeFechar}
      closeOnEsc={podeFechar}
    >
      <ModalOverlay bg="rgba(15,23,42,0.45)" backdropFilter="blur(8px)" />
      <ModalContent
        bg="rgba(255,255,255,0.85)"
        backdropFilter="blur(40px) saturate(200%)"
        sx={{ WebkitBackdropFilter: 'blur(40px) saturate(200%)' }}
        borderWidth="1px"
        borderColor="rgba(255,255,255,0.6)"
        borderRadius={{ base: 'none', md: '3xl' }}
        boxShadow="0 24px 70px -18px rgba(99,102,241,0.30)"
      >
        <ModalHeader>
          <Flex align="center" gap={3}>
            <Flex
              w="44px"
              h="44px"
              borderRadius="xl"
              align="center"
              justify="center"
              color="white"
              bgGradient={`linear(135deg, ${C.indigo}, ${C.pink})`}
            >
              {renderHeaderIcon()}
            </Flex>
            <Box>
              <Text fontSize="17px" fontWeight="700" color={C.ink}>
                {headerTitulo()}
              </Text>
              <Text fontSize="12px" color={C.muted}>
                {empresaLabel || 'Sonnet aplica categoria + POC nos pendentes'}
              </Text>
            </Box>
          </Flex>
        </ModalHeader>
        {podeFechar && <ModalCloseButton onClick={fechar} />}

        <ModalBody>{renderBody()}</ModalBody>

        <ModalFooter gap={3}>
          {fase === 'idle' && (
            <>
              <Button variant="ghost" onClick={fechar} size="sm" borderRadius="full">
                Cancelar
              </Button>
              <Button
                leftIcon={<Sparkle size={16} weight="fill" />}
                borderRadius="full"
                px={5}
                color="white"
                fontWeight="600"
                bgGradient={`linear(135deg, ${C.indigo}, ${C.pink})`}
                _hover={{ filter: 'brightness(1.05)' }}
                isDisabled={alvo <= 0 || !preview.data?.pode_usar_ia}
                onClick={rodar}
              >
                Conciliar {alvo > 0 ? fmtNum(alvo) : ''}
              </Button>
            </>
          )}
          {fase === 'running' && (
            <Button
              variant="outline"
              size="sm"
              borderRadius="full"
              onClick={() => {
                cancelRef.current = true;
              }}
            >
              Cancelar após o lote atual
            </Button>
          )}
          {(fase === 'done' || fase === 'error') && (
            <Button borderRadius="full" px={6} colorScheme="purple" onClick={fechar}>
              Concluído
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
